import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Highcharts from 'highcharts/highmaps';
import HighchartsReact from 'highcharts-react-official';
import { DiagnosisReadDTO } from '../../generated/api-client';
import { extractItalianProvinceHcKey } from '../../utils/italianAddressProvince';
import { isDiagnosisClosed } from '../../utils/diagnosisStatus';

/** GeoJSON province Italia (admin2), come [map collection Highcharts](https://github.com/highcharts/map-collection-dist). */
const IT_PROVINCES_GEOJSON_URL =
  'https://code.highcharts.com/mapdata/countries/it/it-all-all.geo.json';

interface Props {
  diagnoses: DiagnosisReadDTO[];
  addressByDiagnosisId: Record<string, string>;
}

type ProvinceAgg = { total: number; open: number; closed: number };

export const HomeDiagnosisItalyMapCard: React.FC<Props> = ({ diagnoses, addressByDiagnosisId }) => {
  const [topology, setTopology] = React.useState<Record<string, unknown> | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [isUnmatchedModalOpen, setIsUnmatchedModalOpen] = React.useState(false);

  React.useEffect(() => {
    const ac = new AbortController();
    setLoadError(null);
    fetch(IT_PROVINCES_GEOJSON_URL, { signal: ac.signal })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => setTopology(data))
      .catch(err => {
        if (err.name === 'AbortError') return;
        setLoadError(err instanceof Error ? err.message : 'Caricamento mappa fallito');
        setTopology(null);
      });
    return () => ac.abort();
  }, []);

  const { byProvince, unmatchedDiagnoses } = React.useMemo(() => {
    const map = new Map<string, ProvinceAgg>();
    const unmatched: Array<{ id: string; code: string; address: string }> = [];
    for (const d of diagnoses) {
      const id = d.id ?? '';
      const addr = addressByDiagnosisId[id];
      const hcKey = extractItalianProvinceHcKey(addr);
      if (!hcKey) {
        unmatched.push({
          id,
          code: d.table_code_index ?? '—',
          address: addr ?? 'Indirizzo non disponibile',
        });
        continue;
      }
      const row = map.get(hcKey) ?? { total: 0, open: 0, closed: 0 };
      row.total += 1;
      if (isDiagnosisClosed(d)) row.closed += 1;
      else row.open += 1;
      map.set(hcKey, row);
    }
    return { byProvince: map, unmatchedDiagnoses: unmatched };
  }, [diagnoses, addressByDiagnosisId]);
  const unmatchedCount = unmatchedDiagnoses.length;

  const chartOptions = React.useMemo((): Highcharts.Options | null => {
    if (!topology) return null;

    const seriesData: Highcharts.PointOptionsObject[] = [];
    byProvince.forEach((v, hcKey) => {
      seriesData.push({
        'hc-key': hcKey,
        value: v.total,
        open: v.open,
        closed: v.closed,
      } as Highcharts.PointOptionsObject);
    });

    const maxVal = Math.max(1, ...Array.from(byProvince.values()).map(x => x.total));

    const options = {
      chart: {
        map: topology,
        height: 820,
        backgroundColor: 'transparent',
      },
      title: { text: undefined },
      credits: {
        enabled: true,
        text: 'Highcharts / OpenStreetMap',
        href: 'https://www.openstreetmap.org/copyright',
      },
      mapNavigation: { enabled: true },
      colorAxis: {
        min: 0,
        max: maxVal,
        minColor: '#ECEFF1',
        maxColor: '#1565C0',
        labels: {
          style: {
            color: '#FFFFFF',
          },
        },
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          color: '#FFFFFF',
        },
      },
      tooltip: {
        useHTML: true,
        formatter: function tooltipFormatter(this: {
          point: Highcharts.Point & {
            name?: string;
            value?: number;
            open?: number;
            closed?: number;
          };
        }) {
          const pt = this.point;
          const hcKey = (pt.options as { 'hc-key'?: string })['hc-key'] ?? '';
          const provinceCode = hcKey.startsWith('it-') ? hcKey.slice(3).toUpperCase() : '';
          const name = pt.name ?? hcKey ?? '';
          const title = provinceCode ? `${name} (${provinceCode})` : name;
          const total = pt.value ?? 0;
          const open = pt.open ?? (pt.options as { open?: number }).open ?? 0;
          const closed = pt.closed ?? (pt.options as { closed?: number }).closed ?? 0;
          return `<b>${title}</b><br/>Totale: <b>${total}</b><br/>Aperte: ${open}<br/>Chiuse: ${closed}`;
        },
      },
      series: [
        {
          type: 'map',
          data: seriesData,
          joinBy: ['hc-key', 'hc-key'],
          name: 'Diagnosi',
          borderWidth: 0.4,
          borderColor: '#90A4AE',
          nullColor: '#F5F5F5',
          states: { hover: { borderColor: '#37474F', brightness: 0.05 } },
        },
      ],
    };
    return options as unknown as Highcharts.Options;
  }, [topology, byProvince]);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <Stack
        direction="row"
        spacing={3}
        alignItems="baseline"
        sx={{ mb: 1, whiteSpace: 'nowrap', overflowX: 'auto' }}
      >
        <Typography variant="h6">Mappa Italia (per provincia)</Typography>
        <Typography variant="body2" color="text.secondary">
          I conteggi usano la sigla in fondo all’indirizzo, es. <code>(MI)</code>.
        </Typography>
        {unmatchedCount > 0 && (
          <div>
            <Typography variant="caption" color="text.secondary">
              {unmatchedCount} diagnosi senza sigla provincia riconosciuta nell’indirizzo (non
              incluse nella mappa).
            </Typography>
            <Button size="small" onClick={() => setIsUnmatchedModalOpen(true)}>
              Elenca
            </Button>
          </div>
        )}
      </Stack>

      {loadError && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {loadError}. Verifica la rete o un blocco sulla CDN Highcharts.
        </Alert>
      )}

      {!topology && !loadError && <LinearProgress sx={{ mb: 1 }} />}

      {chartOptions && (
        <Box sx={{ width: '100%', minHeight: 520 }}>
          <HighchartsReact
            highcharts={Highcharts}
            constructorType="mapChart"
            options={chartOptions}
            immutable
          />
        </Box>
      )}

      {diagnoses.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Nessuna diagnosi corrispondente ai filtri correnti.
        </Typography>
      )}

      <Dialog
        open={isUnmatchedModalOpen}
        onClose={() => setIsUnmatchedModalOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Diagnosi senza provincia riconosciuta</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            {unmatchedDiagnoses.map(item => (
              <Paper key={`${item.id}-${item.code}`} variant="outlined" sx={{ p: 1.25 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  spacing={1}
                  alignItems="center"
                >
                  <Stack sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.code}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ wordBreak: 'break-word' }}
                    >
                      {item.address}
                    </Typography>
                  </Stack>
                  {item.id ? (
                    <Link component={RouterLink} to={`/diagnosis/${item.id}`} underline="hover">
                      Apri
                    </Link>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      ID mancante
                    </Typography>
                  )}
                </Stack>
              </Paper>
            ))}
            {unmatchedDiagnoses.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Nessuna diagnosi senza provincia.
              </Typography>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};
